/**
 * AIPrefillPanel — "Draft this form with AI" for module create pages.
 *
 * <AIPrefillPanel module="ptw" onDraft={(result) => applyReport} />
 *
 * - Renders null without <module>:create permission or when the
 *   safety_assistant module is disabled for the tenant.
 * - Collapsed teaser → textarea + Generate; staged loading copy with Cancel
 *   (generation takes 5–60s); errors inline (not toasts).
 * - On success calls onDraft(result); the page returns an apply report
 *   ({ applied, skipped, warnings }) which is rendered as the authoritative
 *   "what was filled" record with per-field provenance and certainty.
 */

import React, { useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { usePermissions } from '../../context/PermissionContext';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import useAIPrefill from '../../hooks/useAIPrefill';

const BRAND = '#1B5C94';

function loadingCopy(elapsed) {
  if (elapsed < 5) return 'Analyzing your description…';
  if (elapsed < 20) return "Drafting fields from your site's safety records…";
  return 'Still working — complex drafts can take up to a minute…';
}

const SOURCE_LABELS = {
  user_description: 'from your description',
  document: 'from the attached document',
  llm_knowledge: 'AI knowledge',
};

function sourceLabel(source) {
  if (!source) return 'AI knowledge';
  if (source.startsWith('similar_record:')) return 'from a similar past record';
  if (source.startsWith('master_data:')) return `from master data (${source.split(':')[1]})`;
  return SOURCE_LABELS[source] || source;
}

export default function AIPrefillPanel({
  module,
  onDraft,
  disabled = false,
  disabledHint = '',
  placeholder = 'Describe the work in a sentence or two…',
}) {
  const { hasPermission } = usePermissions();
  const { isModuleEnabled } = useModuleAccess();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [report, setReport] = useState(null);
  const { requestDraft, cancel, status, error, result, elapsedSeconds, reset } =
    useAIPrefill(module);

  // Hourly per-user quota, reported by the backend on each generation. Null
  // until the first draft of the session, and whenever the cap is disabled.
  const rateLimit = result?.rate_limit || null;

  if (!hasPermission(module, 'create')) return null;
  if (!isModuleEnabled('safety_assistant')) return null;

  const generate = async () => {
    setReport(null);
    const data = await requestDraft({ description });
    if (data && onDraft) {
      setReport(onDraft(data) || null);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-4 flex w-full items-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-sm font-medium transition-colors hover:bg-blue-50"
        style={{ borderColor: BRAND, color: BRAND }}
      >
        <Sparkles className="h-4 w-4" />
        Draft this form with AI
      </button>
    );
  }

  return (
    <div className="mb-4 rounded-lg border bg-white p-4 shadow-sm" style={{ borderColor: BRAND }}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold" style={{ color: BRAND }}>
          <Sparkles className="h-4 w-4" /> Draft this form with AI
        </h3>
        <button
          type="button"
          onClick={() => { cancel(); reset(); setOpen(false); }}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close AI prefill"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={placeholder}
        rows={3}
        maxLength={4000}
        disabled={status === 'loading'}
        className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none"
      />

      <div className="mt-2 flex items-center gap-3">
        {status === 'loading' ? (
          <>
            <span className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" style={{ color: BRAND }} />
              {loadingCopy(elapsedSeconds)} ({elapsedSeconds}s)
            </span>
            <button
              type="button"
              onClick={cancel}
              className="text-sm text-gray-500 underline hover:text-gray-700"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={generate}
            disabled={disabled || description.trim().length < 10}
            className="rounded-md px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: BRAND }}
            title={disabled ? disabledHint : undefined}
          >
            Generate draft
          </button>
        )}
        <span className="text-xs text-gray-400">
          Workers, gas readings and checklists are never AI-filled.
        </span>
        {/* Warn as the hourly cap approaches rather than only once it is hit,
            so running out is never a surprise. Silent while there is plenty
            left — a quota readout on every draft would just be noise. */}
        {rateLimit && rateLimit.remaining <= 5 && (
          <span
            className={`text-xs ${rateLimit.remaining === 0 ? 'text-red-600' : 'text-amber-700'}`}
            title={`Limit is ${rateLimit.limit} AI drafts per hour, per user.`}
          >
            {rateLimit.remaining === 0
              ? 'No AI drafts left this hour'
              : `${rateLimit.remaining} of ${rateLimit.limit} AI drafts left this hour`}
          </span>
        )}
      </div>

      {status === 'error' && error && (
        <div className="mt-2 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{error.message}</span>
          {error.status !== 403 && (
            <button type="button" onClick={generate} className="underline">Retry</button>
          )}
        </div>
      )}

      {status === 'success' && report && (
        <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm">
          <p className="font-medium text-gray-800">
            AI filled {report.applied.length} field{report.applied.length === 1 ? '' : 's'}
            {report.skipped.filter((s) => s.reason === 'already-filled').length > 0 &&
              ` · ${report.skipped.filter((s) => s.reason === 'already-filled').length} skipped (already filled by you)`}
          </p>
          <ul className="mt-1 space-y-0.5">
            {report.applied.map((f) => (
              <li key={f.path} className="text-gray-600">
                <span className="font-medium">{f.label}</span>
                <span
                  className="ml-2 text-xs text-gray-400"
                  title={`Source: ${sourceLabel(result?.field_sources?.[f.path])}`}
                >
                  {sourceLabel(result?.field_sources?.[f.path])}
                  {result?.certainty?.[f.path] === 'low' && ' · low confidence'}
                </span>
              </li>
            ))}
          </ul>
          {(report.warnings.length > 0 || (result?.warnings || []).length > 0) && (
            <ul className="mt-2 space-y-0.5 text-xs text-amber-700">
              {[...(result?.warnings || []), ...report.warnings].map((w, i) => (
                <li key={i}>• {w}</li>
              ))}
            </ul>
          )}
          {(report.advisory || []).length > 0 && (
            <div className="mt-2 rounded-md bg-blue-50 p-2 text-xs text-blue-800">
              <p className="font-medium">Suggested hazards (add them on the detail page after creating):</p>
              <ul className="mt-1 list-inside list-disc">
                {report.advisory.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            </div>
          )}
          {result?.rationale && (
            <p className="mt-2 text-xs italic text-gray-500">{result.rationale}</p>
          )}
        </div>
      )}
    </div>
  );
}
